import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Check, X, Filter, Download } from 'lucide-react';
import { expenseApi, Expense } from '../services/api.ts';
import moment from 'moment';
import { SimpleBarChart, SimplePieChart } from '../components/ChartHelpers.tsx';
import ViewToggle, { ViewMode } from '../components/ViewToggle.tsx';

export type PaymentStatusCategory = 'unpaid' | 'processing' | 'paid' | 'unknown';

export const CANONICAL_EXPENSE_STATUSES: readonly string[] = [
  '0. HOLD',
  '1. 已交單，待審批',
  '2. 已支付, 未交單 (無子公司)',
  '3. 待支付 (已交單並審批)',
  '4. 已通知開支票',
  '5. 已開支票並寄出',
  '6. DONE',
];

export function normalizeExpenseStatus(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw === 'HOLD' || raw === '未交單 (HOLD)' || raw === '0. 未交單 (HOLD)') return '0. HOLD';
  if (raw === '已交單, 待AP審批' || raw === '1. 已交單, 待AP審批') return '1. 已交單，待審批';
  return raw;
}

function getPaymentStatusCategory(rawStatus: string): PaymentStatusCategory {
  const s = normalizeExpenseStatus(rawStatus);
  if (!s) return 'unknown';
  if (s.startsWith('0. ')) return 'unpaid';
  if (s.startsWith('6. ')) return 'paid';
  if (s.startsWith('1. ') || s.startsWith('2. ') || s.startsWith('3. ') ||
      s.startsWith('4. ') || s.startsWith('5. ')) return 'processing';
  switch (s) {
    case '未交單': return 'unpaid';
    case '已完成': return 'paid';
    case '待審批':
    case '已支付':
    case '待支付':
    case '已通知':
    case '已寄出': return 'processing';
    case '已批准':
    case '已拒絕': return 'unknown';
    default: return 'unknown';
  }
}

export default function ExpenseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState<string[]>(['6. DONE']);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<'all' | PaymentStatusCategory>('all');

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'report' ? 'report' : 'list';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get('view') === 'report' ? 'report' : 'list';
    if (target !== viewMode) setViewMode(target);
  }, [location.search]);

  const EXPENSE_STATE_KEY = 'expenseListState';
  const EXPENSE_FROM_PAGE_KEY = 'expenseList_fromPage';
  const expenseStateRestoredRef = useRef(false);
  const expenseRestoringRef = useRef(false);

  const saveExpenseState = () => {
    const state = { searchTerm, hiddenStatuses, paymentStatusFilter, viewMode };
    sessionStorage.setItem(EXPENSE_STATE_KEY, JSON.stringify(state));
  };

  const setExpenseFromPage = () => {
    sessionStorage.setItem(EXPENSE_FROM_PAGE_KEY, location.pathname + location.search);
  };

  const restoreExpenseState = () => {
    if (expenseStateRestoredRef.current) return;
    const saved = sessionStorage.getItem(EXPENSE_STATE_KEY);
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      expenseRestoringRef.current = true;
      if (s.searchTerm !== undefined) setSearchTerm(s.searchTerm);
      if (s.hiddenStatuses) setHiddenStatuses(s.hiddenStatuses);
      if (s.paymentStatusFilter) setPaymentStatusFilter(s.paymentStatusFilter);
      if (s.viewMode) setViewMode(s.viewMode);
      expenseStateRestoredRef.current = true;
      setTimeout(() => { expenseRestoringRef.current = false; }, 100);
    } catch {}
  };

  useEffect(() => {
    expenseStateRestoredRef.current = false;
    restoreExpenseState();
    sessionStorage.setItem(EXPENSE_FROM_PAGE_KEY, location.pathname + location.search);
    fetchExpenses();
    return () => { saveExpenseState(); };
  }, [location.pathname]);

  const statusFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const path = e.composedPath?.() as Node[] | undefined;
      const node = statusFilterRef.current;
      if (!node) return;
      const inside = node.contains(t) || (path && path.indexOf(node as unknown as EventTarget) !== -1);
      if (showStatusDropdown && !inside) setShowStatusDropdown(false);
    };
    const handleDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleDocMouseDown, true);
    document.addEventListener('keydown', handleDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown, true);
      document.removeEventListener('keydown', handleDocKeyDown);
    };
  }, [showStatusDropdown]);

  useEffect(() => {
    const filtered = expenses.filter(e => {
      const s = normalizeExpenseStatus(e.status);
      if (s && hiddenStatuses.includes(s)) return false;
      if (paymentStatusFilter !== 'all') {
        const cat = getPaymentStatusCategory(e.status);
        if (cat !== paymentStatusFilter) return false;
      }
      return true;
    });
    setFilteredExpenses(filtered);
  }, [expenses, hiddenStatuses, paymentStatusFilter]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await expenseApi.getAll();
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      if (searchTerm.trim()) {
        const response = await expenseApi.search(searchTerm);
        setExpenses(response.data);
      } else {
        await fetchExpenses();
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('expense.deleteConfirm'))) {
      try {
        await expenseApi.delete(id);
        setExpenses(expenses.filter(e => e.id !== id));
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await expenseApi.approve(id);
      setExpenses(expenses.map(e => 
        e.id === id ? { ...e, status: '已批准' } : e
      ));
    } catch (error) {
      console.error('Approve failed:', error);
    }
  };

  const handleReject = async (id: number) => {
    try {
      await expenseApi.reject(id);
      setExpenses(expenses.map(e => 
        e.id === id ? { ...e, status: '已拒絕' } : e
      ));
    } catch (error) {
      console.error('Reject failed:', error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await expenseApi.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `expenses_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '待審批': return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('status.pending') };
      case '已批准': return { bg: 'bg-green-100', text: 'text-green-800', label: t('status.approved') };
      case '已拒絕': return { bg: 'bg-red-100', text: 'text-red-800', label: t('status.rejected') };
      default: return { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    }
  };

  const expenseReportStats = useMemo(() => {
    const totalAmount = filteredExpenses.reduce((sum, e) => sum + (e.subtotal || 0), 0);
    const typeMap = new Map<string, number>();
    for (const e of filteredExpenses) {
      const purpose = (e.expense_purpose || '').trim() || '(Unspecified)';
      typeMap.set(purpose, (typeMap.get(purpose) || 0) + (e.subtotal || 0));
    }
    const byType = Array.from(typeMap.entries()).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => b.value - a.value);

    const companyMap = new Map<string, number>();
    for (const e of filteredExpenses) {
      const company = (e.expense_company || '').trim() || '(Unspecified)';
      companyMap.set(company, (companyMap.get(company) || 0) + (e.subtotal || 0));
    }
    const byCompany = Array.from(companyMap.entries()).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => b.value - a.value);

    const monthMap = new Map<string, number>();
    for (const e of filteredExpenses) {
      if (e.application_date) {
        const m = moment(e.application_date).format('YYYY-MM');
        monthMap.set(m, (monthMap.get(m) || 0) + (e.subtotal || 0));
      }
    }
    const byMonth = Array.from(monthMap.entries()).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => a.name.localeCompare(b.name));

    return { totalAmount, byType, byCompany, byMonth };
  }, [filteredExpenses]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('nav.expenseApplication')}</h1>
          <p className="text-slate-500 mt-1">{t('expense.manageDesc')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { saveExpenseState(); setExpenseFromPage(); navigate('/expense/new', { state: { from: location.pathname + location.search } }); }}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <Plus className="w-4 h-4" />
            <span>{t('expense.addNew')}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>{exporting ? t('msg.loading') : t('action.export')}</span>
          </button>
        </div>
      </div>

      <ViewToggle
        viewMode={viewMode}
        onChange={setViewMode}
      />

      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('expense.searchPlaceholder')}
                className="pl-10 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                {t('expense.search')}
              </button>
            </div>
            
            {/* Status Filter */}
            <div ref={statusFilterRef} className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-700">{t('expense.statusFilter')}</span>
                <span className="text-xs text-slate-400">({expenses.length - filteredExpenses.length} {t('filter.hidden')})</span>
              </button>
              {showStatusDropdown && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-slate-700">{t('expense.hiddenStatuses')}</span>
                    <button
                      onClick={() => setHiddenStatuses([])}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {t('expense.showAll')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {['0. 未交單 (HOLD)', '1. 已交單, 待AP審批', '2. 已支付, 未交單 (無子公司)',
                      '3. 待支付 (已交單並審批)', '4. 已通知開支票', '5. 已開支票並寄出',
                      '6. DONE'].map(status => (
                      <label key={status} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={hiddenStatuses.includes(status)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setHiddenStatuses([...hiddenStatuses, status]);
                            } else {
                              setHiddenStatuses(hiddenStatuses.filter(s => s !== status));
                            }
                          }}
                          className="mr-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-600">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.applicationDate')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.expenseCompany')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.expensePurpose')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.subtotal')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.approver')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.status')}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('expense.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    {t('expense.loading')}
                  </td>
                </tr>
              ) : filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    {t('expense.noExpenses')}
                  </td>
                </tr>
              ) : (
                filteredExpenses.map((expense) => {
                  const status = getStatusColor(expense.status);
                  return (
                    <tr key={expense.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-slate-600">
                        {moment(expense.application_date).format('YYYY-MM-DD')}
                      </td>
                      <td className="px-6 py-4 text-slate-800">{expense.expense_company}</td>
                      <td className="px-6 py-4 text-slate-600">{expense.expense_purpose}</td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-slate-800">
                          ${expense.subtotal?.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{expense.approver || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.bg} ${status.text}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          {expense.status === '待審批' && (
                            <>
                              <button
                                onClick={() => handleApprove(expense.id)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title={t('expense.approve')}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(expense.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={t('expense.reject')}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => { saveExpenseState(); setExpenseFromPage(); navigate(`/expense/edit/${expense.id}`, { state: { from: location.pathname + location.search } }); }}
                            className="p-2 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(expense.id)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {viewMode === 'report' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-xs text-green-600 uppercase tracking-wider">Total Amount</div>
              <div className="text-2xl font-bold text-green-800 mt-1">${expenseReportStats.totalAmount.toLocaleString()}</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="text-xs text-orange-600 uppercase tracking-wider">Categories</div>
              <div className="text-2xl font-bold text-orange-800 mt-1">{expenseReportStats.byType.length}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-xs text-blue-600 uppercase tracking-wider">Companies</div>
              <div className="text-2xl font-bold text-blue-800 mt-1">{expenseReportStats.byCompany.length}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SimplePieChart
              data={expenseReportStats.byType.slice(0, 6).map(t => ({ name: t.name, value: t.value }))}
              title="Expenses by Type"
            />
            <SimpleBarChart
              data={expenseReportStats.byMonth.length > 0 ? expenseReportStats.byMonth : [{ name: 'No Data', value: 0 }]}
              title="Monthly Expense Trend"
            />
          </div>

          <SimpleBarChart
            data={expenseReportStats.byCompany.slice(0, 10).length > 0 ? expenseReportStats.byCompany.slice(0, 10) : [{ name: 'No Data', value: 0 }]}
            title="Expenses by Company (Top 10)"
          />
        </div>
      )}
    </div>
  );
}
